/**
 * j file upload-dir <path>
 * Upload an entire directory and generate a browsable index page
 * Supports incremental uploads — only changed files are re-uploaded
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { promises as fs } from 'fs';
import { resolve, relative, basename, extname, join, dirname } from 'path';
import { homedir } from 'os';
import { createHash } from 'crypto';
import { ConfigService } from '../../services/config.service.js';
import { SettingsService } from '../../services/settings.service.js';
import { FileService } from '../../services/file.service.js';

const ALLOWED_EXTENSIONS = new Set([
    '.html', '.htm', '.md', '.markdown',
    '.css', '.js', '.mjs',
    '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.ico',
    '.json', '.txt', '.xml',
    '.woff', '.woff2', '.ttf', '.eot',
]);

const DEFAULT_EXCLUDES = [
    'node_modules', '.git', 'dist', '.next', '.nuxt', '.jai1',
    '__pycache__', '.DS_Store', '.env', '.env.local',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MANIFEST_DIR = join(homedir(), '.jai1', 'upload-manifests');

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function generateSlug(name: string): string {
    return name
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase()
        .substring(0, 100);
}

interface FileEntry {
    absolutePath: string;
    relativePath: string;
    size: number;
    mtimeMs: number;
}

interface ManifestEntry {
    size: number;
    mtimeMs: number;
    hash: string;
}

interface Manifest {
    collectionId: number;
    slug: string;
    scope: string;
    projectId: string | null;
    uploadedAt: string;
    files: Record<string, ManifestEntry>;
}

async function scanDirectory(dirPath: string, excludes: string[]): Promise<FileEntry[]> {
    const entries: FileEntry[] = [];

    async function walk(currentPath: string): Promise<void> {
        const items = await fs.readdir(currentPath, { withFileTypes: true });

        for (const item of items) {
            if (excludes.includes(item.name)) continue;
            if (item.name.startsWith('.') && item.name !== '.') continue;

            const fullPath = join(currentPath, item.name);

            if (item.isDirectory()) {
                await walk(fullPath);
            } else if (item.isFile()) {
                const ext = extname(item.name).toLowerCase();
                if (ALLOWED_EXTENSIONS.has(ext)) {
                    const stat = await fs.stat(fullPath);
                    if (stat.size <= MAX_FILE_SIZE) {
                        entries.push({
                            absolutePath: fullPath,
                            relativePath: relative(dirPath, fullPath),
                            size: stat.size,
                            mtimeMs: stat.mtimeMs,
                        });
                    }
                }
            }
        }
    }

    await walk(dirPath);
    return entries.sort((a, b) => a.relativePath.localeCompare(b.relativePath));
}

function getManifestPath(dirPath: string, slug: string): string {
    // Use hash of absolute path to avoid collisions between same slug in different dirs
    const dirHash = createHash('md5').update(dirPath).digest('hex').substring(0, 8);
    return join(MANIFEST_DIR, `${slug}-${dirHash}.json`);
}

async function loadManifest(manifestPath: string): Promise<Manifest | null> {
    try {
        const content = await fs.readFile(manifestPath, 'utf-8');
        return JSON.parse(content);
    } catch {
        return null;
    }
}

async function saveManifest(manifestPath: string, manifest: Manifest): Promise<void> {
    await fs.mkdir(dirname(manifestPath), { recursive: true });
    await fs.writeFile(manifestPath, JSON.stringify(manifest, null, 2));
}

function hashFileContent(content: Buffer): string {
    return createHash('md5').update(content).digest('hex');
}

/**
 * Determine which files need uploading and which were deleted locally
 */
function diffFiles(
    files: FileEntry[],
    manifest: Manifest | null,
): { changed: FileEntry[]; unchanged: number; deleted: string[] } {
    if (!manifest) {
        return { changed: files, unchanged: 0, deleted: [] };
    }

    const changed: FileEntry[] = [];
    let unchanged = 0;

    const currentPaths = new Set(files.map(f => f.relativePath));

    for (const file of files) {
        const prev = manifest.files[file.relativePath];
        if (prev && prev.size === file.size && prev.mtimeMs === file.mtimeMs) {
            unchanged++;
        } else {
            changed.push(file);
        }
    }

    // Detect files that existed in previous manifest but no longer exist locally
    const deleted = Object.keys(manifest.files).filter(p => !currentPaths.has(p));

    return { changed, unchanged, deleted };
}

export function createFileUploadDirCommand(): Command {
    return new Command('upload-dir')
        .description('Upload directory, generate browsable index page')
        .argument('<path>', 'Path to directory')
        .option('--name <name>', 'Collection name (default: directory name)')
        .option('--slug <slug>', 'Custom slug')
        .option('--project-id <id>', 'Project ID (default: auto-detect)')
        .option('--personal', 'Force personal scope')
        .option('--exclude <patterns>', 'Comma-separated patterns to exclude')
        .option('--force', 'Force re-upload all files (ignore cache)')
        .option('--open', 'Open index URL in browser after upload')
        .option('-j, --json', 'JSON output')
        .action(async (dirArg: string, opts) => {
            try {
                const configService = new ConfigService();
                const config = await configService.load();
                if (!config) {
                    console.error(chalk.red('Chưa đăng nhập. Chạy "j auth" trước.'));
                    process.exit(1);
                }

                const dirPath = resolve(dirArg);

                // Validate directory exists
                try {
                    const stat = await fs.stat(dirPath);
                    if (!stat.isDirectory()) {
                        console.error(chalk.red(`"${dirArg}" không phải là thư mục`));
                        process.exit(1);
                    }
                } catch {
                    console.error(chalk.red(`Thư mục "${dirArg}" không tồn tại`));
                    process.exit(1);
                }

                // Determine scope (same logic as upload command)
                let scope: 'personal' | 'project' = 'personal';
                let projectId: string | undefined;

                if (opts.personal) {
                    scope = 'personal';
                } else if (opts.projectId) {
                    scope = 'project';
                    projectId = opts.projectId;
                } else {
                    const settings = new SettingsService();
                    const detectedProjectId = settings.getProjectId();
                    if (detectedProjectId) {
                        scope = 'project';
                        projectId = detectedProjectId;
                    }
                }

                if (!opts.json && scope === 'personal' && !opts.personal) {
                    console.log(chalk.dim('Không detect được project → upload to personal files'));
                }

                // Scan directory
                const excludes = opts.exclude
                    ? [...DEFAULT_EXCLUDES, ...opts.exclude.split(',').map((s: string) => s.trim())]
                    : DEFAULT_EXCLUDES;

                const allFiles = await scanDirectory(dirPath, excludes);

                if (allFiles.length === 0) {
                    console.error(chalk.red('Không tìm thấy file hợp lệ trong thư mục'));
                    process.exit(1);
                }

                const collectionName = opts.name || basename(dirPath);
                const collectionSlug = opts.slug || generateSlug(collectionName);

                // Load manifest for incremental upload
                const manifestPath = getManifestPath(dirPath, collectionSlug);
                const prevManifest = opts.force ? null : await loadManifest(manifestPath);
                const { changed: filesToUpload, unchanged, deleted: deletedPaths } = diffFiles(allFiles, prevManifest);

                const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0);
                const uploadSize = filesToUpload.reduce((sum, f) => sum + f.size, 0);
                const hasWork = filesToUpload.length > 0 || deletedPaths.length > 0;

                if (!opts.json) {
                    console.log(`\nScanning ${chalk.cyan(dirArg)}... found ${chalk.bold(String(allFiles.length))} files (${formatSize(totalSize)})`);
                    if (prevManifest) {
                        const parts: string[] = [];
                        if (unchanged > 0) parts.push(`${unchanged} unchanged`);
                        if (filesToUpload.length > 0) parts.push(`${filesToUpload.length} to upload (${formatSize(uploadSize)})`);
                        if (deletedPaths.length > 0) parts.push(`${deletedPaths.length} to delete`);
                        console.log(chalk.dim(`  ${parts.join(', ')}`));
                    }
                    console.log();
                }

                if (!hasWork) {
                    if (!opts.json) {
                        console.log(chalk.green(`✓ Collection "${collectionName}" is up to date — nothing to upload`));
                        if (prevManifest) {
                            console.log(chalk.dim(`  Use --force to re-upload all files`));
                        }
                        console.log();
                    } else {
                        console.log(JSON.stringify({
                            collection: { slug: collectionSlug, scope },
                            uploaded: 0, skipped: unchanged, deleted: 0, failed: 0,
                            indexUrl: null,
                        }, null, 2));
                    }
                    return;
                }

                const service = new FileService();

                // Create collection (upsert)
                let collection;
                try {
                    collection = await service.createCollection(config, {
                        name: collectionName,
                        slug: collectionSlug,
                        scope,
                        projectId,
                    });
                } catch (createError) {
                    // Fallback to personal if project access denied
                    const isAutoDetect = scope === 'project' && !opts.projectId && !opts.personal;
                    const isAccessDenied = createError instanceof Error && createError.message.includes('not a project member');
                    if (isAutoDetect && isAccessDenied) {
                        if (!opts.json) {
                            console.log(chalk.dim(`Project "${projectId}" chưa đăng ký → upload to personal files`));
                        }
                        scope = 'personal';
                        projectId = undefined;
                        collection = await service.createCollection(config, {
                            name: collectionName,
                            slug: collectionSlug,
                            scope: 'personal',
                        });
                    } else {
                        throw createError;
                    }
                }

                // Upload changed files sequentially
                let uploaded = 0;
                let failed = 0;
                const pad = String(filesToUpload.length).length;
                const newManifestFiles: Record<string, ManifestEntry> = {};

                // Carry over unchanged entries from previous manifest
                if (prevManifest) {
                    for (const file of allFiles) {
                        const prev = prevManifest.files[file.relativePath];
                        if (prev && prev.size === file.size && prev.mtimeMs === file.mtimeMs) {
                            newManifestFiles[file.relativePath] = prev;
                        }
                    }
                }

                for (const file of filesToUpload) {
                    const idx = uploaded + failed + 1;
                    const prefix = `  [${String(idx).padStart(pad)}/${filesToUpload.length}]`;

                    try {
                        const content = await fs.readFile(file.absolutePath);
                        const base64 = content.toString('base64');
                        const filename = basename(file.absolutePath);
                        const hash = hashFileContent(content);

                        await service.upload(config, {
                            content: base64,
                            filename,
                            filePath: file.relativePath,
                            scope: collection.scope as 'personal' | 'project',
                            projectId: collection.projectId || undefined,
                            collectionId: collection.id,
                        });

                        // Track in manifest
                        newManifestFiles[file.relativePath] = {
                            size: file.size,
                            mtimeMs: file.mtimeMs,
                            hash,
                        };

                        uploaded++;
                        if (!opts.json) {
                            console.log(`${prefix} ${file.relativePath} ${chalk.dim(`(${formatSize(file.size)})`)} ${chalk.green('✓')}`);
                        }
                    } catch (err) {
                        failed++;
                        if (!opts.json) {
                            const errMsg = err instanceof Error ? err.message : String(err);
                            console.log(`${prefix} ${file.relativePath} ${chalk.red('✗')} ${chalk.dim(errMsg)}`);
                        }
                    }
                }

                // Delete files that no longer exist locally
                let deleted = 0;
                if (deletedPaths.length > 0) {
                    try {
                        const detail = await service.getCollection(config, collection.id);
                        const fileMap = new Map(detail.files.map((f: any) => [f.filePath, f.id]));
                        for (const delPath of deletedPaths) {
                            const fileId = fileMap.get(delPath);
                            if (fileId) {
                                try {
                                    await service.delete(config, fileId);
                                    deleted++;
                                    if (!opts.json) {
                                        console.log(`  ${chalk.red('✗')} ${delPath} ${chalk.dim('(deleted)')}`);
                                    }
                                } catch {
                                    if (!opts.json) {
                                        console.log(`  ${chalk.yellow('!')} ${delPath} ${chalk.dim('(delete failed)')}`);
                                    }
                                }
                            }
                        }
                    } catch {
                        if (!opts.json) {
                            console.log(chalk.yellow(`  Warning: Không thể xoá ${deletedPaths.length} files đã bị xoá local`));
                        }
                    }
                }

                // Generate index page
                let indexResult;
                try {
                    indexResult = await service.generateIndex(config, collection.id);
                } catch (err) {
                    if (!opts.json) {
                        console.error(chalk.yellow(`\nWarning: Không thể tạo index page: ${err instanceof Error ? err.message : String(err)}`));
                    }
                }

                // Save manifest for next incremental upload
                const manifest: Manifest = {
                    collectionId: collection.id,
                    slug: collectionSlug,
                    scope: collection.scope,
                    projectId: collection.projectId,
                    uploadedAt: new Date().toISOString(),
                    files: newManifestFiles,
                };
                await saveManifest(manifestPath, manifest).catch(() => {});

                // Output result
                if (opts.json) {
                    console.log(JSON.stringify({
                        collection: {
                            id: collection.id,
                            name: collectionName,
                            slug: collectionSlug,
                            scope: collection.scope,
                            projectId: collection.projectId,
                        },
                        uploaded,
                        skipped: unchanged,
                        deleted,
                        failed,
                        totalSize,
                        indexUrl: indexResult?.indexUrl || null,
                    }, null, 2));
                } else {
                    console.log();
                    const parts: string[] = [];
                    if (uploaded > 0) parts.push(`${uploaded} uploaded`);
                    if (deleted > 0) parts.push(`${deleted} deleted`);
                    if (unchanged > 0) parts.push(`${unchanged} unchanged`);
                    if (failed > 0) parts.push(`${failed} failed`);

                    const icon = failed > 0 ? chalk.yellow('✓') : chalk.green('✓');
                    console.log(`${icon} Collection "${collectionName}" (${parts.join(', ')}, ${formatSize(totalSize)})`);
                    if (indexResult?.indexUrl) {
                        console.log(chalk.cyan('  Index:') + ` ${indexResult.indexUrl}`);
                    }
                    console.log(chalk.dim(`  Scope: ${collection.scope}${collection.projectId ? ` | Project: ${collection.projectId}` : ''}`));
                    console.log();
                }

                // Auto-open
                if (opts.open && indexResult?.indexUrl) {
                    const { exec } = await import('child_process');
                    const openCmd = process.platform === 'darwin' ? 'open' : process.platform === 'win32' ? 'start' : 'xdg-open';
                    exec(`${openCmd} "${indexResult.indexUrl}"`);
                }

            } catch (error) {
                console.error(chalk.red(`Lỗi: ${error instanceof Error ? error.message : String(error)}`));
                process.exit(1);
            }
        });
}
